import { collection, deleteField, doc, FieldPath, getDocs, query, serverTimestamp, updateDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { applyTrackEdits } from './studentTracksModel';

const COLLECTION_NAME = 'studentTracks';

/**
 * A student's track pointers, ONE query per student — the same shape as
 * fetchDeliveredNotesForStudent, and for the same reason: the rule resolves
 * family membership through students/{id}, and a query that names studentId is
 * what lets that rule evaluate. Sorted client-side rather than with an orderBy
 * so no composite index has to exist before the first read succeeds (a missing
 * index is FAILED_PRECONDITION, and the Songs page has already been bitten).
 */
export const fetchTracksForStudent = async (studentId) => {
	if (!studentId) return [];
	const snap = await getDocs(query(collection(db, COLLECTION_NAME), where('studentId', '==', studentId)));
	// A pointer hidden with its song (removedAt; repertoireRemovalModel.js) is
	// dropped here, for staff and families alike. Admin track edits (a removed
	// file, a renamed file or song) apply here too, so every surface agrees.
	const rows = snap.docs
		.map((d) => ({ id: d.id, ...d.data() }))
		.filter((r) => !r.removedAt)
		.map(applyTrackEdits);
	const ms = (v) => (v && typeof v.toMillis === 'function' ? v.toMillis() : 0);
	return rows.sort((a, b) => ms(b.updatedAt) - ms(a.updatedAt));
};

/*
 * Admin track edits (2026-09-29; studentTracksModel → applyTrackEdits). The
 * rules refuse all three to anyone but an admin / owner. ONE DOT PATH PER KEY
 * (FieldPath, since an edit key is a hash and a song name is free text): a
 * write of the whole `fileEdits` map would replace every other file's edits.
 * `updatedAt` is deliberately left alone, so an edit does not reorder a list.
 */
const trackRef = (docId) => doc(db, COLLECTION_NAME, docId);
const clean = (s) => (typeof s === 'string' ? s.trim().slice(0, 120) : '');

/** Rename one file; an empty label returns it to its standard name. */
export const setTrackFileLabel = (docId, editKey, label) =>
	updateDoc(trackRef(docId), new FieldPath('fileEdits', editKey, 'label'), clean(label) || deleteField());

/** Hide one file everywhere (restorable). */
export const removeTrackFile = (docId, editKey, uid) =>
	updateDoc(
		trackRef(docId),
		new FieldPath('fileEdits', editKey, 'removedAt'),
		serverTimestamp(),
		new FieldPath('fileEdits', editKey, 'removedBy'),
		uid || null
	);

/** Bring a removed file back, exactly as it was (its label is kept). */
export const restoreTrackFile = (docId, editKey) =>
	updateDoc(
		trackRef(docId),
		new FieldPath('fileEdits', editKey, 'removedAt'),
		deleteField(),
		new FieldPath('fileEdits', editKey, 'removedBy'),
		deleteField()
	);

/** Rename the song a set of tracks is filed under; empty restores the filed name. */
export const setTrackSongName = (docId, name) => updateDoc(trackRef(docId), 'displayName', clean(name) || deleteField());

export default { fetchTracksForStudent, setTrackFileLabel, removeTrackFile, restoreTrackFile, setTrackSongName };
